import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNotify } from 'react-admin';
import {
    deriveGateState,
    type GateDoc,
    type GateMode,
} from '../../types/gates';
import { useGateDecision } from './hooks/useGateDecision';
import { KEEP_DIALOG_SIZE } from '../../root/touchTheme';

function fmtUsd(n: number): string {
    return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

/** Granted cap as display text: a number, or unbounded (null) — GATE.md §3. */
function fmtCap(cap: number | null): string {
    return cap === null ? 'unbounded' : fmtUsd(cap);
}

/**
 * The clearing element. Approval is a STANDING RISK MANDATE, not an
 * execution trigger — the algo trades under the cap whenever it has a fresh
 * target; approving never fires an order (design/GATE.md §1).
 *
 * Three distinct verbs, never folded together (GATE.md §1/§11):
 * - Allow (approve): permit exposure up to a cap, or unbounded.
 * - Hold (deny): freeze at current size — the position keeps riding, never
 *   sold.
 * - Close: sell the position to flat and block re-entry until the decision
 *   changes. Reversible, but commands a trade, so it is confirmed first.
 *
 * - awaiting_decision / increase_requested / editing a settled card: amount
 *   field (empty = no cap/unbounded, shown as a gray placeholder) + Allow /
 *   Hold / Close. increase_requested additionally shows current cap → new
 *   ask; leaving it unanswered is legitimate (old cap keeps being enforced,
 *   no write).
 * - approved: "Approved · $X" / "Approved · unbounded" (+ "algo proposes $Y"
 *   when the outstanding ask is higher but not material enough to bump the
 *   generation) with Edit cap / Hold / Close.
 * - denied: "Held — position keeps riding, not adding" with Allow / Close.
 * - closing: "Closing — selling to flat, no re-entry" with Resume (Allow) /
 *   Hold.
 */
export function GateDecisionCell({
    gate,
    readOnly,
    mode,
}: {
    gate: GateDoc;
    readOnly: boolean;
    mode?: GateMode | null;
}) {
    const theme = useTheme();
    const notify = useNotify();
    const { submit, submitting } = useGateDecision();
    const state = deriveGateState(gate);
    const [editing, setEditing] = useState(false);
    const [amount, setAmount] = useState<string>('');
    const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

    // Empty draft = no cap (unbounded approval). A non-empty draft must be a
    // positive number — a real-money cap must never silently fall back to
    // the algo's proposed amount (useGateDecision's undefined-default is for
    // the no-amount-shown quick actions only, see actQuick below). Allow is
    // disabled — with an inline error — on a non-empty invalid draft.
    const draft = amount.trim();
    const parsedAmount = Number(draft);
    const amountValid =
        draft !== '' && Number.isFinite(parsedAmount) && parsedAmount > 0;
    const amountError = draft !== '' && !amountValid;

    // A new proposal (generation bump / limit change) resets the draft.
    useEffect(() => {
        setAmount('');
        setEditing(false);
    }, [gate.request_generation, gate.proposed_limit_usd]);

    const needsInput =
        state === 'awaiting_decision' || state === 'increase_requested';
    const showControls = !readOnly && (needsInput || editing);
    // bogg already auto-approves an undecided card in auto mode (GATE.md §3);
    // the controls stay available so the operator can still override with
    // Hold/Close, but the prompt should not read as "blocking trading".
    const autoApproved =
        mode === 'auto' &&
        state === 'awaiting_decision' &&
        (gate.decision == null || gate.decision.action == null);

    // Immediate writes (approve/deny) — never require confirmation.
    const submitAction = async (
        action: 'approve' | 'deny' | 'close',
        amountUsd?: number | null
    ) => {
        try {
            await submit(
                gate,
                action,
                action === 'approve' ? amountUsd : undefined
            );
            setEditing(false);
        } catch {
            // Most common cause: bogg bumped request_generation → rules denied.
            // The onSnapshot listener delivers the fresh proposal automatically.
            notify(
                'Decision rejected — the proposal changed, please review the new request',
                { type: 'warning' }
            );
        }
    };

    // Allow/Hold from the amount form use the current draft. Approve is only
    // reachable while the draft is empty (no cap → unbounded, null) or a
    // valid amount — never silently falls back to the proposed amount.
    const actFromForm = (action: 'approve' | 'deny') => {
        const usd = draft === '' ? null : amountValid ? parsedAmount : undefined;
        return submitAction(action, action === 'approve' ? usd : undefined);
    };

    // Quick Allow/Hold from a settled summary (no amount shown) default to
    // the algo's currently proposed cap.
    const actQuick = (action: 'approve' | 'deny') =>
        submitAction(action, undefined);

    const confirmClose = async () => {
        await submitAction('close');
        setCloseConfirmOpen(false);
    };

    const closeDialog = (
        <Dialog
            open={closeConfirmOpen}
            onClose={() => setCloseConfirmOpen(false)}
            className={KEEP_DIALOG_SIZE}
        >
            <DialogTitle>Close this market?</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    This sells the position to flat and blocks re-entry until
                    you change the decision.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setCloseConfirmOpen(false)}>
                    Cancel
                </Button>
                <Button
                    color="error"
                    variant="contained"
                    disabled={submitting}
                    onClick={confirmClose}
                >
                    Close market
                </Button>
            </DialogActions>
        </Dialog>
    );

    if (showControls) {
        return (
            <>
                <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}
                >
                    {state === 'increase_requested' && gate.decision && (
                        <Typography
                            sx={{
                                fontSize: 11,
                                color: theme.palette.warning.main,
                            }}
                        >
                            currently {fmtCap(gate.decision.approved_limit_usd)}{' '}
                            → algo asks {fmtUsd(gate.proposed_limit_usd)}
                        </Typography>
                    )}
                    {autoApproved && (
                        <Typography
                            sx={{
                                fontSize: 11,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            Auto-approved — Hold or Close to override
                        </Typography>
                    )}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                        }}
                    >
                        <TextField
                            size="small"
                            // The app theme's filled variant reserves top
                            // padding for a floating label; without one the
                            // input text sits below the adornment.
                            hiddenLabel
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                            placeholder="no cap"
                            inputProps={{
                                inputMode: 'decimal',
                                style: {
                                    width: 72,
                                    fontFamily: 'monospace',
                                    fontSize: 13,
                                },
                            }}
                            InputProps={{
                                endAdornment: (
                                    <Typography
                                        sx={{
                                            ml: 0.25,
                                            fontSize: 13,
                                            fontFamily: 'monospace',
                                            lineHeight: 1,
                                            color: draft
                                                ? 'text.primary'
                                                : 'text.disabled',
                                        }}
                                    >
                                        $
                                    </Typography>
                                ),
                            }}
                            disabled={submitting}
                            error={amountError}
                            helperText={
                                amountError
                                    ? 'Enter an amount > 0, or leave empty for no cap'
                                    : undefined
                            }
                        />
                        <Tooltip
                            title="One click fills the cap with the algo's proposed amount."
                            placement="top"
                            arrow
                        >
                            <Chip
                                label={`use proposed ${fmtUsd(gate.proposed_limit_usd)}`}
                                size="small"
                                variant="outlined"
                                disabled={submitting}
                                onClick={() =>
                                    // Exact value, not rounded — a rounded
                                    // fill could differ from the actual
                                    // proposal (or hit 0 for sub-$1 asks).
                                    setAmount(String(gate.proposed_limit_usd))
                                }
                                sx={{ fontSize: 10, height: 22 }}
                            />
                        </Tooltip>
                        <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={submitting || amountError}
                            onClick={() => actFromForm('approve')}
                        >
                            Allow
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={submitting}
                            onClick={() => actFromForm('deny')}
                        >
                            Hold
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={submitting}
                            onClick={() => setCloseConfirmOpen(true)}
                        >
                            Close
                        </Button>
                        {editing && (
                            <Button
                                size="small"
                                onClick={() => setEditing(false)}
                            >
                                Cancel
                            </Button>
                        )}
                    </Box>
                </Box>
                {closeDialog}
            </>
        );
    }

    const summary = (() => {
        switch (state) {
            case 'approved':
                return {
                    text:
                        gate.decision?.approved_limit_usd === null
                            ? 'Approved · unbounded'
                            : `Approved · ${fmtUsd(gate.decision!.approved_limit_usd as number)}`,
                    color: theme.palette.success.main,
                    // Non-material bigger ask (no gen bump): show it as info only.
                    info:
                        gate.decision?.approved_limit_usd !== null &&
                        gate.decision !== undefined &&
                        gate.proposed_limit_usd >
                            (gate.decision.approved_limit_usd as number)
                            ? `algo proposes ${fmtUsd(gate.proposed_limit_usd)}`
                            : null,
                };
            case 'denied':
                return {
                    text: 'Held — position keeps riding, not adding',
                    color: theme.palette.warning.main,
                    info: null,
                };
            case 'closing':
                return {
                    text: 'Closing — selling to flat, no re-entry',
                    color: theme.palette.error.main,
                    info: null,
                };
            case 'increase_requested':
                return {
                    text: 'Increase requested',
                    color: theme.palette.warning.main,
                    info: `algo now asks ${fmtUsd(gate.proposed_limit_usd)}`,
                };
            default:
                return {
                    text: 'Awaiting decision',
                    color: theme.palette.text.secondary,
                    info: null,
                };
        }
    })();

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box>
                    <Typography sx={{ fontSize: 12, color: summary.color }}>
                        {summary.text}
                    </Typography>
                    {summary.info && (
                        <Typography
                            sx={{ fontSize: 10, color: 'text.secondary' }}
                        >
                            {summary.info}
                        </Typography>
                    )}
                </Box>
                {!readOnly && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {state === 'approved' && (
                            <>
                                <Button
                                    size="small"
                                    onClick={() => setEditing(true)}
                                >
                                    Edit cap
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    disabled={submitting}
                                    onClick={() => actQuick('deny')}
                                >
                                    Hold
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    disabled={submitting}
                                    onClick={() => setCloseConfirmOpen(true)}
                                >
                                    Close
                                </Button>
                            </>
                        )}
                        {state === 'denied' && (
                            <>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    disabled={submitting}
                                    onClick={() => actQuick('approve')}
                                >
                                    Allow
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    disabled={submitting}
                                    onClick={() => setCloseConfirmOpen(true)}
                                >
                                    Close
                                </Button>
                            </>
                        )}
                        {state === 'closing' && (
                            <>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    disabled={submitting}
                                    onClick={() => actQuick('approve')}
                                >
                                    Resume
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    disabled={submitting}
                                    onClick={() => actQuick('deny')}
                                >
                                    Hold
                                </Button>
                            </>
                        )}
                    </Box>
                )}
            </Box>
            {closeDialog}
        </>
    );
}
